import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useSuspiciousRequestIncidents = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading, error, setError } = useHttp();

  const fetchIncidents = useCallback(
    async ({ page = 1, limit = 25, includeAcknowledged = false } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/suspiciousRequestIncident',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { page, limit, includeAcknowledged },
        });
      } catch (err) {
        toast.error('Fehler beim Laden des IP-Audits');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const fetchRequestStats = useCallback(
    async ({ hours = 24 } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/suspiciousRequestIncident/stats',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { hours },
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Zugriffsstatistik');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const fetchIncidentDetails = useCallback(
    async incidentId => {
      try {
        return await sendRequest({
          url: `/api/v1/suspiciousRequestIncident/${incidentId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der IP-Audit-Details');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const acknowledgeIncident = useCallback(
    async incidentId => {
      try {
        const result = await sendRequest({
          url: `/api/v1/suspiciousRequestIncident/${incidentId}/acknowledge`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        });
        toast.success('IP-Audit-Eintrag abgehakt');
        return result;
      } catch (err) {
        toast.error('Fehler beim Abhaken des IP-Audit-Eintrags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const blacklistIncidentIp = useCallback(
    async incidentId => {
      try {
        const result = await sendRequest({
          url: `/api/v1/suspiciousRequestIncident/${incidentId}/blacklist`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
        });
        toast.success('IP wurde für 24 Stunden gesperrt');
        return result;
      } catch (err) {
        toast.error('Fehler beim Sperren der IP-Adresse');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    setError,
    fetchIncidents,
    fetchRequestStats,
    fetchIncidentDetails,
    acknowledgeIncident,
    blacklistIncidentIp,
  };
};

export default useSuspiciousRequestIncidents;
